import { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { getCshGroupDetail, listCshGroupSummaries } from "../../services/csh/csh.service";
import type { CshGroupSummary } from "../../services/csh/csh.types";
import { buildHomeSiteScope, parseCshGroupIds } from "./homeGroupScope";
import type { HomeSiteScope } from "./homeGroupScope";

export function useHomeGroups(enabled: boolean) {
    const [params, setParams] = useSearchParams();
    const idsParam = params.get("cshGroupIds");
    const selectedIds = useMemo(() => parseCshGroupIds(idsParam), [idsParam]);
    const selectionKey = selectedIds.join(",");
    const [groups, setGroups] = useState<CshGroupSummary[]>([]);
    const [groupsLoading, setGroupsLoading] = useState(enabled);
    const [groupsError, setGroupsError] = useState<string | null>(null);
    const [scopeState, setScopeState] = useState<{
        key: string; scope: HomeSiteScope | null; error: string | null;
    } | null>(null);

    useEffect(() => {
        if (!enabled) return;
        let mounted = true;
        setGroupsLoading(true);
        setGroupsError(null);
        const load = async () => {
            try {
                const response = await listCshGroupSummaries();
                if (!response.success || !response.data) throw new Error(response.error?.message || "Failed to load CSH groups");
                if (mounted) setGroups(response.data);
            } catch (error: unknown) {
                if (mounted) setGroupsError(error instanceof Error ? error.message : "Failed to load CSH groups");
            } finally {
                if (mounted) setGroupsLoading(false);
            }
        };
        void load();
        return () => { mounted = false; };
    }, [enabled]);

    useEffect(() => {
        if (!enabled || !selectionKey) return;
        let mounted = true;
        const load = async () => {
            try {
                const details = await Promise.all(selectedIds.map(async id => {
                    const response = await getCshGroupDetail(id);
                    if (!response.success || !response.data) throw new Error(response.error?.message || `Failed to load CSH group "${id}"`);
                    return response.data;
                }));
                if (mounted) setScopeState({ key: selectionKey, scope: buildHomeSiteScope(details), error: null });
            } catch (error: unknown) {
                if (mounted) setScopeState({
                    key: selectionKey, scope: null,
                    error: error instanceof Error ? error.message : "Failed to load CSH group sites",
                });
            }
        };
        void load();
        return () => { mounted = false; };
    }, [enabled, selectionKey, selectedIds]);

    const options = useMemo(() => [...groups].sort((a, b) => (a.name || "").localeCompare(b.name || "")), [groups]);
    const names = options.filter(group => selectedIds.includes(String(group.id))).map(group => group.name.trim()).filter(Boolean);
    const heading = !selectedIds.length ? "Alarms Monitor"
        : names.length === 1 && selectedIds.length === 1 ? `Alarms Monitor - ${names[0]}`
            : `Alarms Monitor - ${selectedIds.length} CSH Groups`;
    const changeSelection = (ids: string[]) => {
        const next = new URLSearchParams(params);
        if (ids.length) next.set("cshGroupIds", ids.join(","));
        else next.delete("cshGroupIds");
        setParams(next, { replace: true });
    };
    const scopeMatches = scopeState?.key === selectionKey;
    return {
        options, selectedIds, groupsLoading, groupsError, heading,
        summary: names.length ? names.join(", ") : selectedIds.join(", "),
        scope: selectionKey && scopeMatches ? scopeState.scope : null,
        scopeLoading: !!selectionKey && !scopeMatches,
        scopeError: selectionKey && scopeMatches ? scopeState.error : null,
        changeSelection,
    };
}
